import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../utils/setupAxios';

const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

const COIN_FIELDS = ['uid', 'name', 'tid', 'country', 'status'];

function CoinsTable({ coins }) {
  const [selectedUid, setSelectedUid] = useState(null);
  const [coinSnap, setCoinSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const knownFields = coins.length > 0
    ? COIN_FIELDS.filter((f) => coins.some((c) => c[f] != null))
    : [];
  const hasKnownFields = knownFields.length > 0;

  const paged = coins.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const lastPage = Math.max(1, Math.ceil(coins.length / PAGE_SIZE));

  const fetchSnap = useCallback(async (uid) => {
    setSnapLoading(true);
    setSnapError(null);
    setCoinSnap(null);
    try {
      const res = await axiosInstance.get('/coins/users/get-coin-by-uid', { params: { uid } });
      setCoinSnap(res?.data?.data ?? null);
    } catch (e) {
      setSnapError(e?.response?.data?.message || e?.message || 'Failed to load coin snap');
    } finally {
      setSnapLoading(false);
    }
  }, []);

  const handleUidClick = (uid) => {
    if (selectedUid === uid) {
      setSelectedUid(null);
      setCoinSnap(null);
    } else {
      setSelectedUid(uid);
      fetchSnap(uid);
    }
  };

  if (coins.length === 0) {
    return <p className="text-muted px-3">No coins in collection.</p>;
  }

  return (
    <>
      {lastPage > 1 && (
        <div className="d-flex align-items-center px-3 py-2">
          <span className="text-muted small">{coins.length} coins</span>
          <ul className="pagination pagination-sm m-0 ms-auto">
            <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
            </li>
            {Array.from({ length: lastPage }, (_, i) => i + 1)
              .filter((n) => Math.abs(n - page) <= 2)
              .map((n) => (
                <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(n)}>{n}</button>
                </li>
              ))}
            <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
            </li>
          </ul>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-vcenter card-table table-sm">
          <thead>
            <tr>
              {hasKnownFields ? (
                <>
                  {knownFields.map((f) => <th key={f}>{f}</th>)}
                  <th>Actions</th>
                </>
              ) : (
                <th>Data</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paged.map((coin, idx) => (
              <React.Fragment key={coin.uid ?? idx}>
                <tr>
                  {hasKnownFields ? (
                    <>
                      {knownFields.map((f) => (
                        <td key={f}>
                          {f === 'uid' && coin.uid ? (
                            <code
                              className="text-primary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleUidClick(coin.uid)}
                              title="Click to load coin snap"
                            >
                              {coin.uid}
                            </code>
                          ) : (
                            coin[f] != null ? String(coin[f]) : '-'
                          )}
                        </td>
                      ))}
                      <td>
                        {coin.uid && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleUidClick(coin.uid)}
                          >
                            {selectedUid === coin.uid ? 'Hide snap' : 'View snap'}
                          </button>
                        )}
                      </td>
                    </>
                  ) : (
                    <td><pre className="mb-0 small">{JSON.stringify(coin, null, 2)}</pre></td>
                  )}
                </tr>
                {selectedUid === coin.uid && (
                  <tr>
                    <td colSpan={knownFields.length + 1} className="bg-light">
                      {snapLoading && (
                        <div className="py-3 text-center">
                          <div className="spinner-border spinner-border-sm text-muted" role="status" />
                        </div>
                      )}
                      {snapError && <div className="text-danger small p-2">{snapError}</div>}
                      {coinSnap && (
                        <div className="p-3">
                          <div className="row g-2 mb-2">
                            <div className="col-auto">
                              <span className="text-muted small">UID:</span>{' '}
                              <code className="small">{coinSnap.uid}</code>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Name:</span>{' '}
                              <strong className="small">{coinSnap.name ?? '-'}</strong>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Created:</span>{' '}
                              <span className="small">{formatDateTime(coinSnap.created_at)}</span>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Updated:</span>{' '}
                              <span className="small">{formatDateTime(coinSnap.updated_at)}</span>
                            </div>
                          </div>
                          {coinSnap.content && (
                            <div className="mb-2">
                              <div className="text-muted small mb-1">Content (recognition result):</div>
                              <pre className="small bg-white border rounded p-2 mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>
                                {JSON.stringify(coinSnap.content, null, 2)}
                              </pre>
                            </div>
                          )}
                          {coinSnap.static_url && (
                            <div>
                              <div className="text-muted small mb-1">Static URLs:</div>
                              <pre className="small bg-white border rounded p-2 mb-0" style={{ maxHeight: 100, overflow: 'auto' }}>
                                {JSON.stringify(coinSnap.static_url, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function CoinUserDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/coins/users/detail', { params: { user_id: userId } });
      const data = res?.data?.data ?? null;
      if (!data) {
        setError(res?.data?.message || 'User not found');
      } else {
        setDetail(data);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load user detail');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchDetail();
  }, [userId, fetchDetail]);

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all coins for user ${userId}? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const res = await axiosInstance.post('/coins/users/delete-all', { user_id: userId });
      const msg = res?.data?.message || 'All coins deleted.';
      setDeleteMsg({ type: 'success', text: msg });
      fetchDetail();
    } catch (e) {
      setDeleteMsg({ type: 'danger', text: e?.response?.data?.message || e?.message || 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  if (!userId) {
    return (
      <div className="container-xl">
        <div className="alert alert-warning mt-4">
          Missing user_id.{' '}
          <button className="btn btn-link p-0 align-baseline" onClick={() => navigate('/coin-users')}>
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <button className="btn btn-link p-0 text-muted mb-1" onClick={() => navigate('/coin-users')}>
              ← Users
            </button>
            <h2 className="page-title mb-0">
              User ID: <code>{userId}</code>
            </h2>
          </div>
          <div className="col-auto d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchDetail}
              disabled={loading}
            >
              {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Reload'}
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDeleteAll}
              disabled={deleting || loading}
            >
              {deleting ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Delete all coins'}
            </button>
          </div>
        </div>
      </div>

      {deleteMsg && (
        <div className={`alert alert-${deleteMsg.type} alert-dismissible`} role="alert">
          {deleteMsg.text}
          <button type="button" className="btn-close" onClick={() => setDeleteMsg(null)} />
        </div>
      )}

      {loading && !detail && (
        <div className="text-center py-5">
          <div className="spinner-border text-muted" role="status" />
        </div>
      )}

      {error && !detail && (
        <div className="alert alert-danger">{error}</div>
      )}

      {detail && (
        <>
          {/* Info card */}
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="card-title">User Info</h3>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-auto">
                  <div className="text-muted small">Coins count</div>
                  <div className="fs-3 fw-bold">{detail.coins_count ?? 0}</div>
                </div>
                <div className="col-auto">
                  <div className="text-muted small">Created</div>
                  <div>{formatDateTime(detail.created_at)}</div>
                </div>
                <div className="col-auto">
                  <div className="text-muted small">Updated</div>
                  <div>{formatDateTime(detail.updated_at)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Coins collection */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Coin Collection</h3>
              <div className="card-options text-muted small">
                {Array.isArray(detail.coins) ? `${detail.coins.length} items` : ''}
              </div>
            </div>
            <CoinsTable coins={Array.isArray(detail.coins) ? detail.coins : []} />
          </div>
        </>
      )}
    </div>
  );
}
